/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

export default (ref, targetSelector, operation, payload, webComponent, sendToWebComponent = true) => {
    // Reference to the DOM root element required to send events
    if (!ref) {
        return;
    }

    // The event will be sent to the webcomponent and if specified a target that
    // has to be in the same tree as the components parent. It is recommended
    // that the target is a sibling to the
    // current component.

    const receivers = [];

    // If component is embedded in web compnoent, consider the web component as root
    let webComp = ref?.current?.closest(webComponent);
    let root = webComp;
    if (!root) {
        // Otherwise use reference directly
        root = ref?.current;
    }

    // Add webcomponent to list of receivers
    if (sendToWebComponent && root) {
        receivers.push(root);
    }

    // Add target element to list of receivers
    let parent = root?.parentElement;
    if (parent && targetSelector) {
        const target = parent.querySelector(targetSelector);
        if (target) {
            receivers.push(target);
        }
    }

    const createEvent = (name, payload) => {
        return new CustomEvent(name, {
            detail: {
                value: payload
            }
        });
    };

    if (operation === 'update') {
        receivers.forEach(el => el.dispatchEvent(createEvent('cif:update-value', payload)));
    } else if (operation === 'add') {
        receivers.forEach(el => el.dispatchEvent(createEvent('cif:add-value', payload)));
    } else if (operation === 'remove') {
        receivers.forEach(el => el.dispatchEvent(createEvent('cif:remove-value', payload)));
    }

    if (receivers.length > 0 && root && (operation === 'update' || operation === 'add' || operation === 'remove')) {
        setTimeout(() => webComp.dispatchEvent(new CustomEvent('change')), 0);
    }
};
